import type { ContentPackage } from '@didaskill/domain/content-packages/types';
import type { FILE_UPLOAD_CONTEXTS } from '@didaskill/domain/uploads/constants';
import type { Upload } from '@didaskill/domain/uploads/types';

import { HttpRepository } from '../httpRepository';

const e = encodeURIComponent;

export const FILES_API_URL = '/api/v1/file';

export const FILES_PACKAGES_API_URL = `${FILES_API_URL}/packages/:contentPackageId`;

export class FileRepository extends HttpRepository {
  /**
   * Post an image.
   *
   * @param file - File blob object.
   * @param options - Options.
   * @param options.onProgress - Progress event handler. Progress between 0 and 1.
   * @returns Object containing upload metadata for the resized and original image.
   */
  async postImage(
    file: File,
    {
      onProgress,
    }: {
      onProgress?: (progress: number) => void;
    } = {},
  ): Promise<{ resized: Upload; original: Upload }> {
    const req = this.http
      .post('/api/v1/file/images')
      // biome-ignore lint/suspicious/noExplicitAny: DOM Blob type is not compatible with Node.js Blob type expected by superagent
      .attach('file', file as any)
      .set('enctype', 'multipart/form-data');

    if (onProgress) {
      req.on('progress', ({ percent }) => onProgress(percent / 100));
    }

    const { body } = await req;
    return body;
  }

  /**
   * Upload a raw file.
   *
   * @param context - UPLOAD_FILE_CONTEXTS enum.
   * @param file - File object.
   * @param options - Options.
   * @param options.onProgress - Progress event handler. Progress between 0 and 1.
   */
  async uploadRawFile(
    context: FILE_UPLOAD_CONTEXTS,
    file: Blob,
    {
      onProgress,
    }: {
      onProgress?: (progress: number) => void;
    } = {},
  ): Promise<Upload> {
    const req = this.http
      .post(`/api/v1/file/raw/contexts/${e(context)}/files`)
      // biome-ignore lint/suspicious/noExplicitAny: DOM Blob type is not compatible with Node.js Blob type expected by superagent
      .attach('file', file as any);

    if (onProgress) {
      req.on('progress', ({ percent }) => onProgress?.(percent / 100));
    }

    const { body } = await req;

    return body;
  }

  /**
   * Post a SCORM activity content package.
   * @param file - File blob object.
   * @param params - Options.
   * @param params.onProgress - Progress event handler. Progress between 0 and 1.
   * @returns The created ContentPackage.
   */
  async postScormActivityArchive(
    file: Blob,
    {
      onProgress,
    }: {
      onProgress?: (progress: number) => void;
    } = {},
  ): Promise<ContentPackage> {
    const req = this.http
      .post('/api/v1/file/activities/scorm')
      // biome-ignore lint/suspicious/noExplicitAny: DOM Blob type is not compatible with Node.js Blob type expected by superagent
      .attach('file', file as any);

    if (onProgress) {
      req.on('progress', ({ percent }) => onProgress?.(percent / 100));
    }

    const { body } = await req;

    return body;
  }
}
